import { useState } from "react";
import TextField from "@mui/material/TextField";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import * as yup from "yup";
import { useFormik } from "formik";
import Select from "@mui/material/Select";
import OutlinedInput from "@mui/material/OutlinedInput";
import MenuItem from "@mui/material/MenuItem";
import { BsArrowDownCircle, BsArrowUpCircle } from "react-icons/bs";
import { HashLoader } from "react-spinners";

import { MainButton } from "../../components/MainButton";
import { COLORS } from "../../utils/colors";
import { ICONS } from "../../utils/icons";
import { createEvent } from "../../api/events";
import { getOrgByEmail } from "../../api/user";
import { updateUser } from "../../store/mainReducer";
import { useDispatch } from "react-redux";

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 250,
      minWidth: 250,
      fontFamily: "AuthenticSans90",
    },
  },
};

const validationSchema = yup.object({
  name: yup.string("Must be string").required("Required"),
  capacity: yup.number("Must be number").required("Required"),
  max_purchase_amount: yup.number("Must be number").required("Required"),
  start_date: yup.date("Must be valid date").required("Required"),
  end_date: yup.date("Must be valid date").required("Required"),
  max_purchase_amount: yup.number().required("Required"),
  terms: yup
    .string("Must be string")
    .url("Must be a valid URL. Example: https://example.com"),
  privacy_policy: yup
    .string("Must be string")
    .url("Must be a valid URL. Example: https://example.com"),
  refund_policy: yup
    .string("Must be string")
    .url("Must be a valid URL. Example: https://example.com"),
});

const requiredFields1 = [
  {
    label: "Event Name",
    name: "name",
    type: "text",
    required: true,
  },
  {
    label: "Maximum Capacity",
    name: "capacity",
    type: "number",
    required: true,
  },
  {
    label: "Maximum Items per Purchase",
    name: "max_purchase_amount",
    type: "number",
    required: true,
  },
];

const requiredFields2 = [
  {
    label: "Start Date & Time",
    name: "start_date",
    type: "date",
    required: true,
  },
  {
    label: "End Date & Time",
    name: "end_date",
    type: "date",
    required: true,
  },
];

const optionalFields1 = [
  {
    label: "Description",
    name: "description",
    type: "text",
  },
  {
    label: "Currency",
    name: "currency",
    type: "text",
  },
  {
    label: "Overbooking Enabled",
    name: "overbooking",
    type: "singleSelect",
    options: [
      {
        label: "Yes",
        value: true,
      },
      {
        label: "No",
        value: false,
      },
    ],
  },
  {
    label: "Status",
    name: "status",
    type: "singleSelect",
    options: [
      {
        label: "Active",
        value: "active",
      },
      {
        label: "Inactive",
        value: "inactive",
      },
    ],
  },
  {
    label: "Type",
    name: "event_type",
    type: "singleSelect",
    options: [
      {
        label: "General Admission",
        value: "general_admission",
      },
      // {
      //   label: "Seated Event",
      //   value: "seated_event",
      // },
      // {
      //   label: "Combined",
      //   value: "combined",
      // },
    ],
  },
  {
    label: "Gallery",
    name: "gallery",
    type: "file",
    multiple: true,
  },
];

const optionalFields2 = [
  {
    label: "Motto",
    name: "motto",
    type: "text",
  },
  {
    label: "Sale Start",
    name: "sale_start",
    type: "date",
  },

  {
    label: "Sale End",
    name: "sale_end",
    type: "date",
  },
  {
    label: "Checkin Start",
    name: "checkin_start",
    type: "date",
  },
  {
    label: "Checkin End",
    name: "checkin_end",
    type: "date",
  },
  {
    label: "Banner Image",
    name: "banner",
    type: "file",
  },
];

export const CreateEvent = ({ handleSnackbar, supabase, userData }) => {
  const [loading, setLoading] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [banner, setBanner] = useState("");
  const [gallery, setGallery] = useState("");
  const [success, setSuccess] = useState(false);
  const [extraFields, setExtraFields] = useState([]);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      name: "",
      capacity: "",
      max_purchase_amount: "",
      start_date: "",
      end_date: "",
      type: null,
      event_type: null,
      description: null,
      currency: null,
      overbooking: null,
      status: null,
      motto: null,
      sale_start: null,
      sale_end: null,
      checkin_start: null,
      checkin_end: null,
      terms: "",
      privacy_policy: "",
      refund_policy: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  const handleFormSubmit = async (values) => {
    try {
      setLoading(true);

      if (banner) {
        const bannerUrl = await uploadImage(banner);
        values["banner"] = bannerUrl[0];
      }

      if (gallery) {
        const galleryUrls = await uploadImage(gallery);
        values["gallery"] = galleryUrls;
      }

      if (extraFields.length) {
        const extraFieldsObject = extraFields.reduce((acc, item) => {
          if (item.name === "" || item.value === "") {
            handleSnackbar("Please fill in all extra fields", "error");

            throw new Error("Please fill in all extra fields");
          }

          acc[item.name] = item.value;
          return acc;
        }, {});

        values["extra_fields"] = extraFieldsObject;
      }

      const { data, error } = await createEvent(
        values,
        userData.operators[0].api_key
      );

      if (error) {
        handleSnackbar(error.message, "error");
        setLoading(false);
        return;
      }

      if (data) {
        handleSnackbar("Event created successfully", "success");
        setSuccess(true);

        const response = await getOrgByEmail(userData?.email);

        dispatch(updateUser(response.data));
      }

      setLoading(false);
    } catch (err) {
      console.log(err);
      handleSnackbar(
        "Unknown error. Please check all fields & contact us if it persists.",
        "error"
      );
      setLoading(false);
    }
  };

  const handleSelectedFile = (event) => {
    setBanner(event.target.files[0]);
  };

  const handleSelectedGallery = (event) => {
    setGallery(event.target.files);
  };

  const uploadImage = async (incomingFiles) => {
    try {
      setLoading(true);

      let filePaths = [];

      if (incomingFiles?.length) {
        for (let i = 0; i < incomingFiles.length; i++) {
          const file = incomingFiles[i];

          const { data, error } = await supabase.storage
            .from("ooval-bucket")
            .upload(
              `organization_folders/${
                userData?.email.split("@")[1].split(".")[0]
              }/${file.name}`,
              file,
              {
                upsert: true,
              }
            );

          if (error) {
            console.log(error);
            handleSnackbar(error.message, "error");
            setLoading(false);
            return;
          }

          const fileUrl = await getPublicUrl(data.path);
          filePaths.push(fileUrl);
        }
      } else {
        const { data, error } = await supabase.storage
          .from("ooval-bucket")
          .upload(
            `organization_folders/${
              userData?.email.split("@")[1].split(".")[0]
            }/${incomingFiles.name}`,
            incomingFiles,
            {
              upsert: true,
            }
          );

        if (error) {
          console.log(error);
          handleSnackbar(error.message, "error");
          setLoading(false);
          return;
        }

        const fileUrl = await getPublicUrl(data.path);
        filePaths.push(fileUrl);

        setLoading(false);
      }

      return filePaths;
    } catch (err) {
      console.log(err);
      handleSnackbar(err.message, "error");
      setLoading(false);
    }
  };

  const getPublicUrl = async (path) => {
    const { data, error } = supabase.storage
      .from("ooval-bucket")
      .getPublicUrl(`${path}`);

    if (error) {
      console.log(error);
      return;
    }

    return data.publicUrl;
  };

  const addOption = async () => {
    if (
      extraFields[extraFields.length - 1]?.name === "" ||
      extraFields[extraFields.length - 1]?.value === ""
    )
      return handleSnackbar("Please fill in the previous extra field", "error");

    setExtraFields([...extraFields, { name: "", value: "" }]);
  };

  const deleteOption = async (givenIndex) => {
    const newFields = extraFields.filter((item, index) => index !== givenIndex);
    setExtraFields(newFields);
  };

  const handleTextFieldChange = (index, event, field) => {
    const newValues = [...extraFields];

    if (field === "name") {
      newValues[index] = { ...newValues[index], name: event.target.value };
    }

    if (field === "value") {
      newValues[index] = { ...newValues[index], value: event.target.value };
    }

    setExtraFields(newValues);
  };

  return (
    <div style={{ opacity: success && "0.5" }}>
      <div style={{ height: "20px" }} />
      <div className="standard-container">
        <div>
          <div
            className="container-row"
            style={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <div>
              <h2>Go live and sell tickets</h2>
              <p id="subTitle">Fill in the required fields and go live now.</p>
            </div>
          </div>
          <form className="container-column">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <p style={{ margin: "40px 0px 10px 0" }}>Required fields</p>
              {/* <div className="two-columns-fields-container"> */}
              <div style={{ width: "-webkit-fill-available" }}>
                {requiredFields1.map((field, index) => {
                  return (
                    <div key={index}>
                      {field.type === "text" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <TextField
                            fullWidth
                            disabled={loading || success}
                            size="small"
                            name={field.name}
                            placeholder="not set"
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            sx={{
                              "& label": {
                                paddingLeft: (theme) => theme.spacing(1),
                              },
                              "& fieldset": {
                                borderRadius: "6px",
                                border: `2px solid #f0f1f5`,
                                transition: ".2s ease-in-out all",
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#f0f1f5",
                                },
                              "& .MuiOutlinedInput-input:hover": {
                                cursor: loading && "not-allowed",
                              },
                            }}
                          />
                        </div>
                      )}
                      {field.type === "number" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <TextField
                            fullWidth
                            type="number"
                            inputProps={{ min: 0 }}
                            disabled={loading || success}
                            size="small"
                            name={field.name}
                            placeholder="not set"
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            sx={{
                              "& label": {
                                paddingLeft: (theme) => theme.spacing(1),
                              },
                              "& fieldset": {
                                borderRadius: "6px",
                                border: `2px solid #f0f1f5`,
                                transition: ".2s ease-in-out all",
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#f0f1f5",
                                },
                              "& .MuiOutlinedInput-input:hover": {
                                cursor: loading && "not-allowed",
                              },
                            }}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ width: "-webkit-fill-available" }}>
                {requiredFields2.map((field, index) => {
                  return (
                    <div key={index}>
                      {field.type === "text" && (
                        <div className="field-container">
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <TextField
                            fullWidth
                            disabled={loading || success}
                            size="small"
                            className="input"
                            name={field.name}
                            placeholder="not set"
                            value={formik.values[field.name]}
                            onChange={formik.handleChange}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            sx={{
                              "& label": {
                                paddingLeft: (theme) => theme.spacing(1),
                              },
                              "& fieldset": {
                                borderRadius: "6px",
                                border: `2px solid #f0f1f5`,
                                transition: ".2s ease-in-out all",
                              },
                              "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#f0f1f5",
                                },
                              "& .MuiOutlinedInput-input:hover": {
                                cursor: loading && "not-allowed",
                              },
                            }}
                          />
                        </div>
                      )}
                      {field.type === "date" && (
                        <div className="field-container" key={index}>
                          <p
                            id="subheader"
                            style={{
                              margin: "0px 0px 3px",
                              alignSelf: "center",
                            }}
                          >
                            {field.label}*
                          </p>
                          <DateTimePicker
                            name="start_date"
                            disabled={loading || success}
                            value={formik.values[field.name]}
                            onChange={(newValue) =>
                              formik.setValues({
                                ...formik.values,
                                [field.name]: newValue,
                              })
                            }
                            minDate={dayjs(new Date())}
                            error={
                              formik.touched[field.name] &&
                              Boolean(formik.errors[field.name])
                            }
                            helperText={
                              formik.touched[field.name] &&
                              formik.errors[field.name]
                            }
                            renderInput={(params) => (
                              <TextField
                                size="small"
                                name="start_date"
                                disabled={loading || success}
                                {...params}
                                className={"text-field"}
                                error={
                                  formik.touched[field.name] &&
                                  Boolean(formik.errors[field.name])
                                }
                                helperText={
                                  formik.touched[field.name] &&
                                  formik.errors[field.name]
                                }
                                sx={{
                                  width: "-webkit-fill-available",
                                  "& fieldset": {
                                    border: "2px solid",
                                    borderRadius: "6px",
                                    borderColor: COLORS.background,
                                    height: "43px",
                                  },
                                  "& input": {
                                    height: "29px",
                                    borderRadius: "6px",
                                    padding: "5.5px 14px",
                                  },
                                  "&:hover fieldset": {
                                    transition: "0.2s",
                                  },
                                }}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* </div> */}
              {showOptionalFields ? (
                <p
                  style={{
                    width: "-webkit-fill-available",
                    margin: "20px 0px",
                    cursor: "pointer",
                    alignItems: "center",
                    display: "flex",
                    gap: "5px",
                  }}
                  onClick={() => setShowOptionalFields(!showOptionalFields)}
                >
                  <BsArrowUpCircle size={18} /> Hide Optional Fields
                </p>
              ) : (
                <p
                  style={{
                    width: "-webkit-fill-available",
                    margin: "20px 0px",
                    cursor: "pointer",
                    alignItems: "center",
                    display: "flex",
                    gap: "5px",
                  }}
                  onClick={() =>
                    !success && setShowOptionalFields(!showOptionalFields)
                  }
                >
                  <BsArrowDownCircle size={18} /> Show Optional Fields
                </p>
              )}
              {showOptionalFields && (
                <>
                  <div className="two-columns-fields-container">
                    <div style={{ width: "-webkit-fill-available" }}>
                      {optionalFields1.map((field, index) => {
                        return (
                          <div key={index}>
                            {field.type === "text" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <TextField
                                  fullWidth
                                  disabled={loading || success}
                                  size="small"
                                  className="input"
                                  name={field.name}
                                  placeholder="not set"
                                  value={formik.values[field.name]}
                                  onChange={formik.handleChange}
                                  error={
                                    formik.touched[field.name] &&
                                    Boolean(formik.errors[field.name])
                                  }
                                  helperText={
                                    formik.touched[field.name] &&
                                    formik.errors[field.name]
                                  }
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                />
                              </div>
                            )}
                            {field.type === "date" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <DateTimePicker
                                  name="start_date"
                                  disabled={loading || success}
                                  value={formik.values[field.name]}
                                  onChange={(newValue) =>
                                    formik.setValues({
                                      ...formik.values,
                                      [field.name]: newValue,
                                    })
                                  }
                                  minDate={dayjs(new Date())}
                                  renderInput={(params) => (
                                    <TextField
                                      size="small"
                                      name="startDate"
                                      disabled={loading || success}
                                      {...params}
                                      className={"text-field"}
                                      sx={{
                                        "& fieldset": {
                                          border: "2px solid",
                                          borderRadius: "6px",
                                          borderColor: COLORS.background,
                                          height: "43px",
                                        },
                                        "& input": {
                                          height: "29px",
                                          borderRadius: "6px",
                                          padding: "5.5px 14px",
                                        },
                                        "&:hover fieldset": {
                                          transition: "0.2s",
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            )}
                            {field.type === "singleSelect" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <Select
                                  size="small"
                                  name={field.name}
                                  disabled={loading || success}
                                  value={formik.values[field.name]}
                                  onChange={formik.handleChange}
                                  input={<OutlinedInput label="Name" />}
                                  MenuProps={MenuProps}
                                  style={{
                                    maxWidth: "100px",
                                    minWidth: "-webkit-fill-available",
                                  }}
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                >
                                  {field.options.map((option, index) => {
                                    return (
                                      <MenuItem
                                        value={option.value}
                                        key={index}
                                        style={{ minHeight: "40px" }}
                                      >
                                        {option.label}
                                      </MenuItem>
                                    );
                                  })}
                                </Select>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ width: "-webkit-fill-available" }}>
                      {optionalFields2.map((field, index) => {
                        return (
                          <div key={index}>
                            {field.type === "text" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <TextField
                                  fullWidth
                                  disabled={loading || success}
                                  size="small"
                                  className="input"
                                  name={field.name}
                                  placeholder="not set"
                                  value={formik.values[field.name]}
                                  onChange={formik.handleChange}
                                  error={
                                    formik.touched[field.name] &&
                                    Boolean(formik.errors[field.name])
                                  }
                                  helperText={
                                    formik.touched[field.name] &&
                                    formik.errors[field.name]
                                  }
                                  sx={{
                                    "& label": {
                                      paddingLeft: (theme) => theme.spacing(1),
                                    },
                                    "& fieldset": {
                                      borderRadius: "6px",
                                      border: `2px solid #f0f1f5`,
                                      transition: ".2s ease-in-out all",
                                    },
                                    "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                      {
                                        borderColor: "#f0f1f5",
                                      },
                                    "& .MuiOutlinedInput-input:hover": {
                                      cursor: loading && "not-allowed",
                                    },
                                  }}
                                />
                              </div>
                            )}
                            {field.type === "date" && (
                              <div className="field-container">
                                <p
                                  id="subheader"
                                  style={{
                                    margin: "0px 0px 3px",
                                    alignSelf: "center",
                                  }}
                                >
                                  {field.label}
                                </p>
                                <DateTimePicker
                                  name={field.name}
                                  disabled={loading || success}
                                  value={formik.values[field.name]}
                                  onChange={(newValue) =>
                                    formik.setValues({
                                      ...formik.values,
                                      [field.name]: newValue,
                                    })
                                  }
                                  minDate={dayjs(new Date())}
                                  renderInput={(params) => (
                                    <TextField
                                      size="small"
                                      name="startDate"
                                      disabled={loading || success}
                                      {...params}
                                      className={"text-field"}
                                      error={
                                        formik.touched[field.name] &&
                                        Boolean(formik.errors[field.name])
                                      }
                                      helperText={
                                        formik.touched[field.name] &&
                                        formik.errors[field.name]
                                      }
                                      sx={{
                                        "& fieldset": {
                                          border: "2px solid",
                                          borderRadius: "6px",
                                          borderColor: COLORS.background,
                                          height: "43px",
                                        },
                                        "& input": {
                                          height: "29px",
                                          borderRadius: "6px",
                                          padding: "5.5px 14px",
                                        },
                                        "&:hover fieldset": {
                                          transition: "0.2s",
                                        },
                                      }}
                                    />
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Banner Image
                    </p>
                    <input
                      type="file"
                      disabled={loading || success}
                      className="input-upload"
                      onChange={handleSelectedFile}
                      placeholder="Upload logo"
                    />
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Gallery
                    </p>
                    <input
                      multiple
                      disabled={loading || success}
                      type="file"
                      className="input-upload"
                      onChange={handleSelectedGallery}
                      placeholder="Upload logo"
                    />
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "0px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Extra Fields
                    </p>

                    {extraFields.map((field, index) => {
                      return (
                        <div
                          key={index}
                          className="two-columns-fields-container"
                          style={{ margin: "5px 0" }}
                        >
                          <div style={{ width: "-webkit-fill-available" }}>
                            <p id="subheader">-field name-</p>
                            <TextField
                              fullWidth
                              disabled={loading}
                              size="small"
                              placeholder="not set"
                              value={field.name}
                              onChange={(event) =>
                                handleTextFieldChange(index, event, "name")
                              }
                              error={
                                formik.touched.max_usage &&
                                Boolean(formik.errors.max_usage)
                              }
                              helperText={
                                formik.touched.max_usage &&
                                formik.errors.max_usage
                              }
                              sx={{
                                "& label": {
                                  paddingLeft: (theme) => theme.spacing(1),
                                },
                                "& fieldset": {
                                  borderRadius: "6px",
                                  border: `2px solid #f0f1f5`,
                                  transition: ".2s ease-in-out all",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#f0f1f5",
                                  },
                                "& .MuiOutlinedInput-input:hover": {
                                  cursor: loading && "not-allowed",
                                },
                              }}
                            />
                          </div>
                          <div style={{ width: "-webkit-fill-available" }}>
                            <p id="subheader">-field value-</p>
                            <TextField
                              fullWidth
                              disabled={loading}
                              size="small"
                              placeholder="not set"
                              value={field.value}
                              onChange={(event) =>
                                handleTextFieldChange(index, event, "value")
                              }
                              error={
                                formik.touched.max_usage &&
                                Boolean(formik.errors.max_usage)
                              }
                              helperText={
                                formik.touched.max_usage &&
                                formik.errors.max_usage
                              }
                              sx={{
                                "& label": {
                                  paddingLeft: (theme) => theme.spacing(1),
                                },
                                "& fieldset": {
                                  borderRadius: "6px",
                                  border: `2px solid #f0f1f5`,
                                  transition: ".2s ease-in-out all",
                                },
                                "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#f0f1f5",
                                  },
                                "& .MuiOutlinedInput-input:hover": {
                                  cursor: loading && "not-allowed",
                                },
                              }}
                            />
                          </div>
                          <div>
                            <div style={{ height: "18px" }} />
                            <MainButton
                              disabled={loading}
                              color={COLORS.white}
                              textColor={COLORS.black}
                              shadowColor={"transparent"}
                              hoverColor={COLORS.veryLightGrey}
                              textAlignment={"center"}
                              onClickAction={() => deleteOption(index)}
                              width={"fit-content"}
                            >
                              {ICONS.trashBlack20}
                            </MainButton>
                          </div>
                        </div>
                      );
                    })}
                    <MainButton
                      disabled={loading}
                      color={COLORS.white}
                      textColor={COLORS.black}
                      shadowColor={COLORS.black}
                      borderColor={COLORS.black}
                      textAlignment={"center"}
                      onClickAction={() => addOption()}
                      width={"fit-content"}
                    >
                      {loading ? (
                        <>
                          <HashLoader size={20} color={COLORS.grey} /> Add Extra
                          Field
                        </>
                      ) : (
                        "Add Extra Field"
                      )}
                    </MainButton>
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Terms URL
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="terms"
                      placeholder="not set"
                      value={formik.values.terms}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.terms && Boolean(formik.errors.terms)
                      }
                      helperText={formik.touched.terms && formik.errors.terms}
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Privacy Policy URL
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="privacy_policy"
                      placeholder="not set"
                      value={formik.values.privacy_policy}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.privacy_policy &&
                        Boolean(formik.errors.privacy_policy)
                      }
                      helperText={
                        formik.touched.privacy_policy &&
                        formik.errors.privacy_policy
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Refund Policy URL
                    </p>
                    <TextField
                      fullWidth
                      size="small"
                      name="refund_policy"
                      placeholder="not set"
                      value={formik.values.refund_policy}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.refund_policy &&
                        Boolean(formik.errors.refund_policy)
                      }
                      helperText={
                        formik.touched.refund_policy &&
                        formik.errors.refund_policy
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div>
                  {/* <div className="field-container">
                    <p
                      id="subheader"
                      style={{
                        margin: "5px 0px 3px",
                        alignSelf: "center",
                      }}
                    >
                      Whitelist - coming soon
                    </p>
                    <TextField
                      fullWidth
                      disabled={true}
                      size="small"
                      name="whitelist"
                      placeholder="not set"
                      value={formik.values.whitelist}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.whitelist &&
                        Boolean(formik.errors.whitelist)
                      }
                      helperText={
                        formik.touched.whitelist && formik.errors.whitelist
                      }
                      sx={{
                        "& label": {
                          paddingLeft: (theme) => theme.spacing(1),
                        },
                        "& fieldset": {
                          borderRadius: "6px",
                          border: `2px solid #f0f1f5`,
                          transition: ".2s ease-in-out all",
                        },
                        "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                          {
                            borderColor: "#f0f1f5",
                          },
                        "& .MuiOutlinedInput-input:hover": {
                          cursor: loading && "not-allowed",
                        },
                      }}
                    />
                  </div> */}
                </>
              )}
              <div
                className="container-row"
                style={{
                  width: "min-content",
                  alignSelf: "end",
                }}
              >
                <MainButton
                  text={
                    loading ? (
                      <HashLoader color={COLORS.white} size={20} />
                    ) : (
                      "Create"
                    )
                  }
                  disabled={loading || success}
                  color={COLORS.black}
                  textColor={COLORS.white}
                  textHoverColor={COLORS.lightGrey}
                  hoverColor={COLORS.black}
                  shadowColor={COLORS.mainAccent}
                  onClickAction={() => formik.handleSubmit()}
                />
              </div>
            </LocalizationProvider>
          </form>
        </div>
      </div>
    </div>
  );
};
